import React, { useState, useEffect } from 'react';
import { InputNumber, Slider, Tooltip, Button, message } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { COLORS } from '../consts';
import { getRipenessData, putRipenessData } from '../utils/api';
import { LoadingIcon } from './';

const RipenessCriteria = ({
  metaData,
  fetchGraphData,
  currentPlot,
  currentCrop,
  currentDate,
}) => {
  const [loading, setLoading] = useState(false);
  const [ripenessData, setRipenessData] = useState([]);
  const [ripenessIndex, setRipenessIndex] = useState(null);
  const [ripenessPercent, setRipenessPercent] = useState(null);
  const [maxRipenessPercent, setMaxRipenessPercent] = useState(null);
  const [ripenessGap, setRipenessGap] = useState(null);
  const [minSize, setMinSize] = useState(null);
  const [isLoosePlot, setIsLoosePlot] = useState(false);



  // API Functions START
  const fetchRipenessData = async (plot, crop, date) => {
    try {
      setLoading(true);
      const ripenessRes = await getRipenessData(plot, crop, date);
      const data = ripenessRes?.data?.data;
      setRipenessData(data || []);
    } catch (err) {
      const msg =
        err?.response?.data?.message || 'Oops! Error fetching ripeness data!';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const updateRipenessData = async (mode = 'individual') => {
    try {
      const body = {
        mode: mode,
        plot_id: currentPlot.id,
        crop_id: currentCrop.id,
        harvest_date: currentDate,
        ripeness_criteria: {
          default_ripeness_index: ripenessIndex,
          default_ripeness_percentage: ripenessPercent,
          max_ripeness_percentage: maxRipenessPercent,
          default_ripeness_gap: ripenessGap,
          default_minimum_size_in_cm: minSize,
        },
      };
      const res = await putRipenessData(body);

      let msg;
      switch (mode) {
        case 'individual':
          msg = `Updated ripeness criteria for ${currentPlot.name} - ${currentDate}`;
          break;
        case 'across-dates':
          msg = `Updated ripeness criteria for all dates for ${currentPlot.name}`;
          break;
        case 'across-plots':
          msg = `Updated ripeness criteria for all plots for ${currentDate}`;
          break;
      }
      message.success(msg);
      fetchGraphData();
    } catch (err) {
      const msg =
        err?.response?.data?.message || 'Oops! Error updating ripeness data!';
      message.error(msg);
    }
  };

  // API Functions END

    // Fetch ripeness data on plot, crop or date change
  useEffect(() => {
    setIsLoosePlot(metaData?.is_loose_harvest ? metaData.is_loose_harvest : false);
  }, [metaData]);

  // Fetch ripeness data on plot, crop or date change
  useEffect(() => {
    const fn = async () => {
      if (currentCrop && currentPlot && currentDate) {
        await fetchRipenessData(currentPlot.id, currentCrop.id, currentDate);
      } else {
        setRipenessData([]);
      }
    };
    fn();
  }, [currentPlot, currentCrop, currentDate]);

  // Update variables on ripenessData change
  useEffect(() => {
    if (ripenessData) {
      setRipenessIndex(ripenessData.default_ripeness_index || null);
      setRipenessGap(ripenessData.default_ripeness_gap || null);
      setRipenessPercent(ripenessData.default_ripeness_percentage || null);
      setMaxRipenessPercent(ripenessData.max_ripeness_percentage || null);
      setMinSize(ripenessData.default_minimum_size_in_cm);
    }
  }, [ripenessData]);

  const RadioButton = ({ idx, onChange, color }) => (
    <label className='flex flex-col mx-1 items-center  text-base cursor-pointer mb-2 hover:shadow-2xl transition-shadow duration-300 transform hover:scale-110'>
      <input
        type='radio'
        name='ripenessRadio'
        onChange={onChange}
        checked={ripenessIndex == idx}
        value={idx}
        className='appearance-none'
      />
      {idx}
      <div
        className={`h-5 sm:h-4 w-5 sm:w-4 border-solid border-4 rounded-full cursor-pointer flex items-center justify-center hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101`}
        style={{ borderColor: `${color}` }}
      >
        <div
          className='h-2 sm-h-2 w-2 sm:w-2 rounded-full hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101'
          style={ripenessIndex == idx ? { backgroundColor: `${color}` } : {}}
        />
      </div>
    </label>
  );

  return (
    <>
      {loading ? (
        <LoadingIcon />
      ) : (
        <div className="w-full mb-10" id="ripeness">
          <p className="w-full text-center font-IBMPlexSansBold text-2xl mb-5 heading">
            {/*Harvest criteria : {isLoosePlot ? 'Loose' : 'Truss'} Tomatoes*/}
            Harvest Criteria
          </p>
          <div className="flex md:flex-col items-center justify-around mb-2">
            {metaData?.show_ripeness_index && (
              <div className="flex flex-col w-[25%] sm:w-full md:mb-5 items-center justify-center ripenessIndex">
                <div className="flex items-center">
                  {metaData?.colors?.map((color, idx) => (
                    <RadioButton
                      key={idx + 1}
                      onChange={(e) => setRipenessIndex(e.target.value)}
                      color={color}
                      idx={idx + 1}
                    />
                  ))}
                </div>
                <div className="flex justify-center items-centers">
                  <p className="text-base sm:text-sm font-IBMPlexSansBold mr-1">
                    Ripeness index
                  </p>
                  <Tooltip
                    title="Select what color the fruits need to be for harvesting."
                    color={COLORS.primaryBlue}
                  >
                    <InfoCircleOutlined className="text-primaryBlue cursor-pointer" />
                  </Tooltip>
                </div>
              </div>
            )}
            {metaData?.show_ripeness_percentage && !currentPlot?.is_loose && (
              <div className="flex flex-col w-[25%] sm:w-full md:mb-5 items-center justify-center ripenessPercent">
                <div className="flex items-center mb-2">
                  <InputNumber
                    min={1}
                    max={maxRipenessPercent}
                    value={ripenessPercent}
                    onChange={setRipenessPercent}
                    className='rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101'
                  />
                  <p className="text-base font-IBMPlexSansBold px-2 ">out of</p>
                  <InputNumber
                    value={maxRipenessPercent}
                    min={1}
                    max={100}
                    onChange={setMaxRipenessPercent}
                    className='rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-101'
                  />
                </div>
                <div className="flex justify-center items-center">
                  <p className="text-base sm:text-sm font-IBMPlexSansBold mr-1">
                    Ripe fruits per truss
                  </p>
                  <Tooltip
                    title="Select how many fruits in truss need to be ripe for harvesting. "
                    color={COLORS.primaryBlue}
                  >
                    <InfoCircleOutlined className="text-primaryBlue cursor-pointer rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-110" />
                  </Tooltip>
                </div>
              </div>
            )}
            {metaData?.show_ripeness_gap && !currentPlot?.is_loose && (
              <div className="flex flex-col w-[25%] sm:w-full items-center justify-center ripenessGap">
                <div className="flex items-center">
                  <Slider
                    min={metaData.min_ripeness_gap}
                    max={metaData.max_ripeness_gap}
                    className="w-40 text-primaryBlue hover:shadow-2xl transition-shadow duration-100 transform hover:scale-150"
                    onChange={setRipenessGap}
                    value={ripenessGap}
                  />
                </div>
                <div className="flex justify-center items-centers">
                  <p className="text-base sm:text-sm font-IBMPlexSansBold mr-1">
                    Fruit Maturity Gap
                  </p>
                  <Tooltip
                    title="Select permissible maturity difference between oldest and new fruits in the truss."
                    color={COLORS.primaryBlue}
                  >
                    <InfoCircleOutlined className="text-primaryBlue cursor-pointer hover:shadow-2xl transition-shadow duration-300 transform hover:scale-110" />
                  </Tooltip>
                </div>
              </div>
            )}
            {metaData.show_minimum_size_in_cm && !currentPlot?.is_loose && (
              <div className="flex flex-col w-[25%] sm:w-full items-center justify-center ripenessSize">
                <div className="flex items-center">
                  <Slider
                    min={metaData.min_minimum_size_in_cm}
                    max={metaData.max_minimum_size_in_cm}
                    className="w-40 text-primaryBlue hover:shadow-2xl transition-shadow duration-300 transform hover:scale-150"
                    onChange={setMinSize}
                    value={minSize}
                  />
                </div>
                <div className="flex justify-center items-centers">
                  <p className="text-base sm:text-sm font-IBMPlexSansBold mr-1">
                    Minimum size of crop(cm)
                  </p>
                  <Tooltip
                    title="Select minimum size of crop to be considered for harvest."
                    color={COLORS.primaryBlue}
                  >
                    <InfoCircleOutlined className="text-primaryBlue cursor-pointer rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-110" />
                  </Tooltip>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-center sm:flex-col buttons">
            <Tooltip
              title="Set ripeness criteria for all plots for selected date"
              color={COLORS.primaryBlue}
            >
              <div className="flex justify-center items-center mr-2 sm:mb-2">
                <Button
                  type="primary"
                  className='rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105'
                  onClick={() => updateRipenessData("across-plots")}
                >
                  Apply Changes Across Harvest
                </Button>
              </div>
            </Tooltip>
            <Tooltip
              title="Set ripeness criteria for selected plot and date"
              color={COLORS.primaryBlue}
            >
              <div className="flex justify-center items-center mr-2 sm:mb-2">
                <Button type="primary" className='rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105' onClick={() => updateRipenessData()}>
                  Apply Changes
                </Button>
              </div>
            </Tooltip>
            <Tooltip
              title="Set ripeness criteria for all dates for selected plot"
              color={COLORS.primaryBlue}
            >
              <div className="flex justify-center items-center">
                <Button
                  type="primary"
                  className={`rounded-md hover:shadow-2xl transition-shadow duration-300 transform hover:scale-105`}
                  onClick={() => updateRipenessData("across-dates")}
                >
                  Apply Changes Across Plot
                </Button>
              </div>
            </Tooltip>
          </div>
        </div>
      )}
    </>
  );
};

export default RipenessCriteria;
